import axios from "axios"
import { BASE_URL } from "../constants"
import { CloseCircleOutlined } from "@ant-design/icons"
import { Modal, notification } from "antd"
import { convertAPIDatesToLocal, convertAPIDatesToUTC, convertLocalDateStringToUTC, isLocalDateStringValue } from "../utils/dateUtils"
const baseUrl = BASE_URL + '/api'

class AjaxService {

    getUrl = (url) => baseUrl + url

    /**
     * Converts date strings in FormData to UTC
     * @param {FormData} formData - The FormData to convert
     * @returns {FormData} - New FormData with converted dates
     */
    convertFormDataDatesToUTC = (formData) => {
        if (!(formData instanceof FormData)) {
            return formData;
        }

        const newFormData = new FormData();
        
        // Date field names that should be converted
        const dateFields = [
            'created_at', 'updated_at', 'deleted_at', 'payment_date',
            'closed_date', 'start_time', 'end_time', 'sent_at', 'dob',
            'date', 'datetime', 'timestamp', 'day', 'time_start', 'time_end'
        ];
        
        // Iterate through all entries in FormData
        for (const [key, value] of formData.entries()) {
            // Check if the value is a date string
            if (typeof value === 'string') {
                // Check if it's a date field or looks like a date string
                const isDateField = dateFields.some(field => 
                    key.toLowerCase().includes(field.toLowerCase())
                );
                
                if (isDateField || isLocalDateStringValue(value)) {
                    // Convert to UTC
                    const converted = convertLocalDateStringToUTC(value);
                    newFormData.append(key, converted);
                } else {
                    // Keep original value
                    newFormData.append(key, value);
                }
            } else if (value instanceof File || value instanceof Blob) {
                // Keep files as-is
                newFormData.append(key, value);
            } else {
                // Convert other types to string
                newFormData.append(key, String(value));
            }
        }
        
        return newFormData;
    }

    async get(url, signal = null) {

        const token = localStorage.getItem('token');

        let headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }

        let options = { method: 'GET', headers: headers }
        if (signal) {
            options.signal = signal;
        }

        return fetch(this.getUrl(url), options)
            .then(async (response) => {

                const pathname = new URL(response.url).pathname;
                if (pathname === '/login') {
                    return Modal.warning({
                        title: 'Token Expired',
                        content: 'Your token has expired. Please log in again.',
                        onOk: () => {
                            localStorage.removeItem('token');
                            localStorage.removeItem('name');
                            localStorage.removeItem('role');
                            localStorage.removeItem('courtID');
                            window.location.reload(false);
                        },
                    });
                }

                if (response.ok) {
                    const jsonData = await response.json();
                    // Convert UTC dates to local timezone
                    return convertAPIDatesToLocal(jsonData);
                } else {
                    const result = await response.json();
                    if (result.message) {
                        notification.open({ message: result.message, icon: <CloseCircleOutlined style={{ color: 'red' }} /> })
                    }

                    return convertAPIDatesToLocal(result);
                }
            })
            .then(data => {
                return data;
            })
            .catch(async error => {
                if (error.name === 'AbortError') {
                    // Request was cancelled, don't log as error
                    return { success: false, aborted: true };
                }
                console.error("error get data:", error)
            })
    }

    async delete(url, id, signal = null) {

        const token = localStorage.getItem('token');

        let headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }

        let options = { method: 'DELETE', headers: headers }
        if (signal) {
            options.signal = signal;
        }

        return fetch(this.getUrl(url) + id, options)
            .then(async response => {
                if (response.ok) {
                    const jsonData = await response.json();
                    return convertAPIDatesToLocal(jsonData);
                }
                throw response
            })
            .then(data => {
                return data;
            })
            .catch(error => {
                if (error.name === 'AbortError') {
                    return { success: false, aborted: true };
                }
                console.error("error deleting data:", error)
            })
    }

    async post(url, data = null, signal = null) {

        const token = localStorage.getItem('token');
        const isFormData = data instanceof FormData;

        const headers = {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        };

        let requestBody;
        if (isFormData) {
            delete headers['Content-Type'];
            // Convert dates in FormData to UTC
            requestBody = this.convertFormDataDatesToUTC(data);
        } else {
            // Convert dates in JSON data to UTC
            const convertedData = convertAPIDatesToUTC(data);
            requestBody = JSON.stringify(convertedData);
        }

        const options = {
            method: 'POST',
            headers: headers,
            body: requestBody
        };
        if (signal) {
            options.signal = signal;
        }

        return fetch(this.getUrl(url), options)
            .then(async (response) => {
                if (response.ok) {
                    const jsonData = await response.json();
                    return convertAPIDatesToLocal(jsonData);
                } else {
                    const result = await response.json();
                    return convertAPIDatesToLocal(result);
                }
            })
            .then((result) => {
                return result
            })
            .catch(error => {
                if (error.name === 'AbortError') {
                    return { success: false, aborted: true };
                }
                console.error("error post data:", error)
            })
    }

    async postFormData(url, id, data = null, signal = null) {

        const token = localStorage.getItem('token');
        const isFormData = data instanceof FormData;

        const headers = {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        };

        let requestBody;
        if (isFormData) {
            delete headers['Content-Type'];
            // Convert dates in FormData to UTC
            requestBody = this.convertFormDataDatesToUTC(data);
        } else {
            // Convert dates in JSON data to UTC
            const convertedData = convertAPIDatesToUTC(data);
            requestBody = JSON.stringify(convertedData);
        }

        const options = {
            method: 'POST',
            headers: headers,
            body: requestBody
        };
        if (signal) {
            options.signal = signal;
        }

        return fetch(this.getUrl(url)+ id, options)
            .then(async (response) => {
                if (response.ok) {
                    const jsonData = await response.json();
                    return convertAPIDatesToLocal(jsonData);
                } else {
                    const result = await response.json();
                    return convertAPIDatesToLocal(result);
                }
            })
            .then((result) => {
                return result
            })
            .catch(error => {
                if (error.name === 'AbortError') {
                    return { success: false, aborted: true };
                }
                console.error("error post data:", error)
            })
    }

    async putFormData(url, id, data = null, signal = null) {

        const token = localStorage.getItem('token');
        const isFormData = data instanceof FormData;

        const headers = {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        };

        let requestBody;
        if (isFormData) {
            delete headers['Content-Type'];
            // Convert dates in FormData to UTC
            requestBody = this.convertFormDataDatesToUTC(data);
        } else {
            // Convert dates in JSON data to UTC
            const convertedData = convertAPIDatesToUTC(data);
            requestBody = JSON.stringify(convertedData);
        }

        const options = {
            method: 'PUT',
            headers: headers,
            body: requestBody
        };
        if (signal) {
            options.signal = signal;
        }

        return fetch(this.getUrl(url) + id, options)
            .then(async (response) => {
                if (response.ok) {
                    const jsonData = await response.json();
                    return convertAPIDatesToLocal(jsonData);
                } else {
                    const result = await response.json();
                    return convertAPIDatesToLocal(result);
                }
            })
            .then(result => {
                return result
            }).catch(error => {
                if (error.name === 'AbortError') {
                    return { success: false, aborted: true };
                }
                console.error("error put data:", error)
            })
    }

    async put(url, id, data, signal = null) {

        const token = localStorage.getItem('token');
        const isFormData = data instanceof FormData;

        const headers = {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`
        };

        let requestBody;
        if (isFormData) {
            // Convert dates in FormData to UTC
            requestBody = this.convertFormDataDatesToUTC(data);
        } else {
            headers['Content-Type'] = 'application/json';
            // Convert dates in JSON data to UTC
            const convertedData = convertAPIDatesToUTC(data);
            requestBody = JSON.stringify(convertedData);
        }

        const options = {
            method: 'PUT',
            headers: headers,
            body: requestBody
        };
        if (signal) {
            options.signal = signal;
        }

        return fetch(this.getUrl(url) + id, options)
            .then(async (response) => {
                if (response.ok) {
                    const jsonData = await response.json();
                    return convertAPIDatesToLocal(jsonData);
                } else {
                    const result = await response.json();
                    return convertAPIDatesToLocal(result);
                }
            })
            .then(result => {
                return result
            }).catch(error => {
                if (error.name === 'AbortError') {
                    return { success: false, aborted: true };
                }
                console.error("error put data:", error)
            })
    }

    /**
     * Public GET request - no Authorization header. Use for public pages (Terms, Privacy).
     */
    async getPublic(url, signal = null) {
        const headers = { 'Content-Type': 'application/json' };
        const options = { method: 'GET', headers };
        if (signal) {
            options.signal = signal;
        }
        return fetch(this.getUrl(url), options)
            .then(async (response) => {
                if (response.ok) {
                    const jsonData = await response.json();
                    return convertAPIDatesToLocal(jsonData);
                }
                const result = await response.json().catch(() => ({}));
                return result;
            })
            .catch(error => {
                if (error.name === 'AbortError') {
                    return { success: false, aborted: true };
                }
                console.error('error get public:', error);
                return null;
            });
    }

    /**
     * Fetch CMS page content by type (e.g. 'terms-and-policy', 'privacy-policy').
     * Public endpoint - no auth required. Calls Laravel API: GET pages-cms/{type}
     */
    async getPageByType(type) {
        return this.getPublic('/pages-cms/' + encodeURIComponent(type));
    }

    async getAccessToken(email, password) {

        let data = {
            email: email,
            password: password,
            grant_type: 'password'
        };

        const uri = `${baseUrl}/admin/login`;
        let headers = {
            'Accept': 'application/json',
        };
        let options = {
            method: 'POST',
            headers: headers,
            url: uri,
            data: data,
        }

        return axios(options).then(response => {

            if (!response.status) {
                return response;
            } else {
                return response.data;
            }
        }).catch(function (error) {
            console.error("error fetchinng data:", error)
            return error.response.data
        });
    }
}

const ajaxService = new AjaxService()

export default ajaxService